/* eslint-disable max-len */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SealOfBiliteracyContactModel } from 'src/types/SealOfBiliteracyContactModel';
import { SealOfBiliteracyFileListItemModel } from 'src/types/SealOfBiliteracyFileListItemModel';
import { SealOfBiliteracyFormViewModel } from 'src/types/SealOfBiliteracyFormViewModel';
import { SealOfBiliteracyListItemModel } from 'src/types/SealOfBiliteracyListItemModel';
import { SealOfBiliteracyModel } from 'src/types/SealOfBiliteracyModel';
import { SelectOption } from 'src/types/SelectOption';
import { isEmptyObject } from '../../utilities/helperUtilities';
import type { RootState } from '../store';
import {
  createSealOfBiliteracyContact,
  deleteSealOfBiliteracyFileThunk,
  downloadSealOfBiliteracyFileThunk,
  fetchAllSealOfBiliteracyFilesThunk,
  fetchSealOfBiliteracyById,
  fetchSealOfBiliteracyContact,
  fetchSealOfBiliteracyFilesByStudentThunk,
  fetchSealOfBiliteracyFormViewModel,
  fetchSealOfBiliteracyListItems,
  fetchSealOfBiliteracySchoolYears,
  softDeleteSealOfBiliteracyById,
  updateSealOfBiliteracyContact,
  uploadSealOfBiliteracyFileThunk,
} from '../thunks/sealOfBiliteracyThunks';

interface SealOfBiliteracyState {
  loadingListItems: boolean;
  listItems: SealOfBiliteracyListItemModel[];
  loadingDetail: boolean;
  detail: SealOfBiliteracyModel | null;
  loadingContact: boolean;
  contact: SealOfBiliteracyContactModel | null;
  loadingForm: boolean;
  form: SealOfBiliteracyFormViewModel | null;
  loadingSchoolYears: boolean;
  schoolYears: SelectOption[];
  loadingFiles: boolean;
  files: SealOfBiliteracyFileListItemModel[];
  uploadingFile: boolean;
  downloadingFile: boolean;
  deletingFile: boolean;
  loadingStudentFiles: boolean;
  studentFiles: SealOfBiliteracyFileListItemModel[];
  deletingRecord: boolean;
  error: string;
}

// Initial contact form values
export const initialContactFormValues = {
  id: '',
  leaIdentifier: '',
  firstName: '',
  lastOrSurname: '',
  email: '',
  telephone: '',
  documentsUploaded: false,
};

const defaultListState: SealOfBiliteracyListItemModel[] = [];
const defaultContactState: SealOfBiliteracyContactModel =
  initialContactFormValues;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const defaultDetailState: SealOfBiliteracyModel = {
  id: '',
  studentIdentifier: '',
  studentLastOrSurname: '',
  studentFirstName: '',
  studentMiddleName: '',
};

export const initialState: SealOfBiliteracyState = {
  loadingListItems: false,
  listItems: defaultListState,
  loadingDetail: false,
  detail: null,
  loadingContact: false,
  contact: defaultContactState,
  loadingForm: false,
  form: null,
  loadingSchoolYears: false,
  schoolYears: [],
  loadingFiles: false,
  files: [],
  uploadingFile: false,
  downloadingFile: false,
  deletingFile: false,
  loadingStudentFiles: false,
  studentFiles: [],
  deletingRecord: false,
  error: '',
};

export const sealOfBiliteracySlice = createSlice({
  name: 'sealOfBiliteracy',
  initialState,
  reducers: {
    setLoadingListItems: (state, action: PayloadAction<boolean>) => {
      state.loadingListItems = action.payload;
    },
    setListItems: (
      state,
      action: PayloadAction<SealOfBiliteracyListItemModel[]>
    ) => {
      state.listItems = action.payload;
    },
    setLoadingDetail: (state, action: PayloadAction<boolean>) => {
      state.loadingDetail = action.payload;
    },
    setDetail: (state, action: PayloadAction<SealOfBiliteracyModel | null>) => {
      state.detail = action.payload;
    },
    clearDetail: (state) => {
      state.detail = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = '';
    },
    // Contact reducers
    setLoadingContactList: (state, action: PayloadAction<boolean>) => {
      state.loadingContact = action.payload;
    },
    setContact: (
      state,
      action: PayloadAction<SealOfBiliteracyContactModel | null>
    ) => {
      state.contact = action.payload;
    },
    addContact: (
      state,
      action: PayloadAction<SealOfBiliteracyContactModel>
    ) => {
      if (state.contact) {
        state.contact = action.payload;
      }
    },
    updateContact: (
      state,
      action: PayloadAction<SealOfBiliteracyContactModel>
    ) => {
      if (state.contact && state.contact.id === action.payload.id) {
        state.contact = action.payload;
      }
    },
    // Form view model reducers
    setLoadingForm: (state, action: PayloadAction<boolean>) => {
      state.loadingForm = action.payload;
    },
    setForm: (
      state,
      action: PayloadAction<SealOfBiliteracyFormViewModel | null>
    ) => {
      state.form = action.payload;
    },
    clearForm: (state) => {
      state.form = null;
    },
    // File reducers
    setLoadingFiles: (state, action: PayloadAction<boolean>) => {
      state.loadingFiles = action.payload;
    },
    setFiles: (
      state,
      action: PayloadAction<SealOfBiliteracyFileListItemModel[]>
    ) => {
      state.files = action.payload;
    },
    addFile: (
      state,
      action: PayloadAction<SealOfBiliteracyFileListItemModel>
    ) => {
      state.files.push(action.payload);
    },
    updateFile: (
      state,
      action: PayloadAction<SealOfBiliteracyFileListItemModel>
    ) => {
      const index = state.files.findIndex(
        (file) => file.id === action.payload.id
      );
      if (index !== -1) {
        state.files[index] = action.payload;
      }
    },
    removeFile: (state, action: PayloadAction<string>) => {
      state.files = state.files.filter((file) => file.id !== action.payload);
    },
    setUploadingFile: (state, action: PayloadAction<boolean>) => {
      state.uploadingFile = action.payload;
    },
    setDownloadingFile: (state, action: PayloadAction<boolean>) => {
      state.downloadingFile = action.payload;
    },
    setDeletingFile: (state, action: PayloadAction<boolean>) => {
      state.deletingFile = action.payload;
    },
    // Student-specific files reducers
    setLoadingStudentFiles: (state, action: PayloadAction<boolean>) => {
      state.loadingStudentFiles = action.payload;
    },
    setStudentFiles: (
      state,
      action: PayloadAction<SealOfBiliteracyFileListItemModel[]>
    ) => {
      state.studentFiles = action.payload;
    },
    addStudentFile: (
      state,
      action: PayloadAction<SealOfBiliteracyFileListItemModel>
    ) => {
      state.studentFiles.push(action.payload);
    },
    updateStudentFile: (
      state,
      action: PayloadAction<SealOfBiliteracyFileListItemModel>
    ) => {
      const index = state.studentFiles.findIndex(
        (file) => file.id === action.payload.id
      );
      if (index !== -1) {
        state.studentFiles[index] = action.payload;
      }
    },
    removeStudentFile: (state, action: PayloadAction<string>) => {
      state.studentFiles = state.studentFiles.filter(
        (file) => file.id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    // fetch Seal of Biliteracy list items
    builder.addCase(fetchSealOfBiliteracyListItems.pending, (state) => {
      state.listItems = defaultListState;
      state.loadingListItems = true;
      state.error = '';
    });
    builder.addCase(
      fetchSealOfBiliteracyListItems.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.listItems = action.payload;
        }

        state.loadingListItems = false;
      }
    );
    builder.addCase(
      fetchSealOfBiliteracyListItems.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingListItems = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch seal of biliteracy list items';
      }
    );

    // fetch Seal of Biliteracy by ID
    builder.addCase(fetchSealOfBiliteracyById.pending, (state) => {
      state.loadingDetail = true;
      state.error = '';
    });
    builder.addCase(fetchSealOfBiliteracyById.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.detail = action.payload;
      }
      state.loadingDetail = false;
    });
    builder.addCase(fetchSealOfBiliteracyById.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingDetail = false;
      }
      state.error =
        action.error.message || 'Failed to fetch seal of biliteracy detail';
    });

    //fetch Seal of Biliteracy contact
    builder.addCase(fetchSealOfBiliteracyContact.pending, (state, action) => {
      // Only show loading if showLoading is true (default behavior)
      const showLoading = action.meta.arg?.showLoading !== false;
      if (showLoading) {
        state.loadingContact = true;
      }
      state.error = '';
    });
    builder.addCase(fetchSealOfBiliteracyContact.fulfilled, (state, action) => {
      if ('data' in action.payload) {
        state.contact = action.payload.data;
      } else {
        state.contact = {
          id: '',
          leaIdentifier: '',
          firstName: '',
          lastOrSurname: '',
          email: '',
          telephone: '',
          documentsUploaded: false,
        };
      }
      state.loadingContact = false;
    });
    builder.addCase(fetchSealOfBiliteracyContact.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingContact = false;
      }
      state.error =
        action.error.message || 'Failed to fetch seal of biliteracy contact';
    });

    //fetch Seal of Biliteracy school years
    builder.addCase(fetchSealOfBiliteracySchoolYears.pending, (state) => {
      state.schoolYears = [];
      state.loadingSchoolYears = true;
      state.error = '';
    });
    builder.addCase(
      fetchSealOfBiliteracySchoolYears.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.schoolYears = action.payload;
        }
        state.loadingSchoolYears = false;
      }
    );
    builder.addCase(
      fetchSealOfBiliteracySchoolYears.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingSchoolYears = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch seal of biliteracy school years';
      }
    );

    //Update Seal of Biliteracy contact
    builder.addCase(updateSealOfBiliteracyContact.pending, (state) => {
      state.error = '';
    });
    builder.addCase(
      updateSealOfBiliteracyContact.fulfilled,
      (state, action) => {
        if (action.payload) {
          state.contact = action.payload;
        }
      }
    );
    builder.addCase(updateSealOfBiliteracyContact.rejected, (state, action) => {
      state.error =
        action.error.message || 'Failed to update seal of biliteracy contact';
    });

    //Create Seal of Biliteracy contact
    builder.addCase(createSealOfBiliteracyContact.pending, (state) => {
      state.error = '';
    });
    builder.addCase(
      createSealOfBiliteracyContact.fulfilled,
      (state, action) => {
        state.contact = action.payload;
      }
    );
    builder.addCase(createSealOfBiliteracyContact.rejected, (state, action) => {
      state.error =
        action.error.message || 'Failed to create seal of biliteracy contact';
    });

    //fetch Seal of Biliteracy form view model
    builder.addCase(fetchSealOfBiliteracyFormViewModel.pending, (state) => {
      state.loadingForm = true;
      state.error = '';
    });
    builder.addCase(
      fetchSealOfBiliteracyFormViewModel.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.form = action.payload;
        }
        state.loadingForm = false;
      }
    );
    builder.addCase(
      fetchSealOfBiliteracyFormViewModel.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingForm = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch seal of biliteracy form view model';
      }
    );

    builder.addCase(uploadSealOfBiliteracyFileThunk.pending, (state) => {
      state.uploadingFile = true;
      state.error = '';
    });
    builder.addCase(uploadSealOfBiliteracyFileThunk.fulfilled, (state) => {
      state.uploadingFile = false;
    });
    builder.addCase(
      uploadSealOfBiliteracyFileThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.uploadingFile = false;
          state.error =
            action.error.message || 'Failed to upload file. Please try again.';
        }
      }
    );

    // Delete file
    builder.addCase(deleteSealOfBiliteracyFileThunk.pending, (state) => {
      state.deletingFile = true;
      state.error = '';
    });
    builder.addCase(deleteSealOfBiliteracyFileThunk.fulfilled, (state) => {
      state.deletingFile = false;
    });
    builder.addCase(
      deleteSealOfBiliteracyFileThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.deletingFile = false;
        }
        state.error = action.error.message || 'Failed to delete file';
      }
    );

    // Fetch all files
    builder.addCase(fetchAllSealOfBiliteracyFilesThunk.pending, (state) => {
      state.loadingFiles = true;
      state.error = '';
    });
    builder.addCase(
      fetchAllSealOfBiliteracyFilesThunk.fulfilled,
      (state, action) => {
        state.files = action.payload || [];
        state.loadingFiles = false;
      }
    );
    builder.addCase(
      fetchAllSealOfBiliteracyFilesThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFiles = false;
        }
        state.error = action.error.message || 'Failed to fetch files';
      }
    );

    // Download file
    builder.addCase(downloadSealOfBiliteracyFileThunk.pending, (state) => {
      state.downloadingFile = true;
      state.error = '';
    });
    builder.addCase(downloadSealOfBiliteracyFileThunk.fulfilled, (state) => {
      state.downloadingFile = false;
    });
    builder.addCase(
      downloadSealOfBiliteracyFileThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.downloadingFile = false;
        }
        state.error = action.error.message || 'Failed to download file';
      }
    );

    // Fetch files by student
    builder.addCase(
      fetchSealOfBiliteracyFilesByStudentThunk.pending,
      (state, action) => {
        const showLoading = action.meta.arg?.showLoading !== false;
        if (showLoading) {
          state.loadingStudentFiles = true;
        }
        state.error = '';
      }
    );
    builder.addCase(
      fetchSealOfBiliteracyFilesByStudentThunk.fulfilled,
      (state, action) => {
        state.studentFiles = action.payload || [];
        state.loadingStudentFiles = false;
      }
    );
    builder.addCase(
      fetchSealOfBiliteracyFilesByStudentThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingStudentFiles = false;
        }
        state.error = action.error.message || 'Failed to fetch student files';
      }
    );

    // Soft delete record
    builder.addCase(softDeleteSealOfBiliteracyById.pending, (state) => {
      state.deletingRecord = true;
      state.error = '';
    });
    builder.addCase(softDeleteSealOfBiliteracyById.fulfilled, (state) => {
      state.deletingRecord = false;
    });
    builder.addCase(
      softDeleteSealOfBiliteracyById.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.deletingRecord = false;
        }
        state.error = action.error.message || 'Failed to delete record';
      }
    );
  },
});

export const {
  setLoadingListItems,
  setListItems,
  setLoadingDetail,
  setDetail,
  clearDetail,
  setError,
  clearError,
  setLoadingContactList,
  setContact,
  addContact,
  updateContact,
  setLoadingForm,
  setForm,
  clearForm,
  setLoadingFiles,
  setFiles,
  addFile,
  updateFile,
  removeFile,
  setUploadingFile,
  setDownloadingFile,
  setDeletingFile,
  setLoadingStudentFiles,
  setStudentFiles,
  addStudentFile,
  updateStudentFile,
  removeStudentFile,
} = sealOfBiliteracySlice.actions;

// Selectors
export const selectSealOfBiliteracyLoadingListItems = (
  state: RootState
): boolean => state.sealOfBiliteracy.loadingListItems;
export const selectSealOfBiliteracyListItems = (
  state: RootState
): SealOfBiliteracyListItemModel[] => state.sealOfBiliteracy.listItems;
export const selectSealOfBiliteracyLoadingDetail = (
  state: RootState
): boolean => state.sealOfBiliteracy.loadingDetail;
export const selectSealOfBiliteracyDetail = (
  state: RootState
): SealOfBiliteracyModel | null => state.sealOfBiliteracy.detail;
export const selectSealOfBiliteracyError = (state: RootState): string =>
  state.sealOfBiliteracy.error;

// Contact selectors
export const selectSealOfBiliteracyLoadingContact = (
  state: RootState
): boolean => state.sealOfBiliteracy.loadingContact;
export const selectSealOfBiliteracyContact = (
  state: RootState
): SealOfBiliteracyContactModel | null => state.sealOfBiliteracy.contact;

// Form view model selectors
export const selectSealOfBiliteracyLoadingForm = (state: RootState): boolean =>
  state.sealOfBiliteracy.loadingForm;
export const selectSealOfBiliteracyForm = (
  state: RootState
): SealOfBiliteracyFormViewModel | null => state.sealOfBiliteracy.form;

// File selectors
export const selectSealOfBiliteracyLoadingFiles = (state: RootState): boolean =>
  state.sealOfBiliteracy.loadingFiles;
export const selectSealOfBiliteracyFiles = (
  state: RootState
): SealOfBiliteracyFileListItemModel[] => state.sealOfBiliteracy.files;
export const selectSealOfBiliteracyUploadingFile = (
  state: RootState
): boolean => state.sealOfBiliteracy.uploadingFile;
export const selectSealOfBiliteracyDownloadingFile = (
  state: RootState
): boolean => state.sealOfBiliteracy.downloadingFile;
export const selectSealOfBiliteracyDeletingFile = (state: RootState): boolean =>
  state.sealOfBiliteracy.deletingFile;

// School years selectors
export const selectSealOfBiliteracyLoadingSchoolYears = (
  state: RootState
): boolean => state.sealOfBiliteracy.loadingSchoolYears;
export const selectSealOfBiliteracySchoolYears = (
  state: RootState
): SelectOption[] => state.sealOfBiliteracy.schoolYears;

export const selectSealOfBiliteracyCurrentSchoolYear = (
  state: RootState
): string | null => {
  const schoolYear = state.sealOfBiliteracy.schoolYears;

  if(!schoolYear || schoolYear.length === 0) {
    return null;
  }

  const mostRecentSchoolYear = schoolYear[0].value;
  return mostRecentSchoolYear;
}

// Student-specific files selectors
export const selectSealOfBiliteracyLoadingStudentFiles = (
  state: RootState
): boolean => state.sealOfBiliteracy.loadingStudentFiles;
export const selectSealOfBiliteracyStudentFiles = (
  state: RootState
): SealOfBiliteracyFileListItemModel[] => state.sealOfBiliteracy.studentFiles;

// Delete selectors
export const selectSealOfBiliteracyDeletingRecord = (
  state: RootState
): boolean => state.sealOfBiliteracy.deletingRecord;

export default sealOfBiliteracySlice.reducer;
